import { useInviteToken } from '@/components/auth/useInviteToken';
import { useGetAccountWithTokenQuery } from '@/services/accounts';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Locale } from 'types';

export interface UseTranslationOptions {
  useAccountPreference?: boolean;
  useAnonymously?: boolean;
}

export function useTranslation(options?: UseTranslationOptions) {
  const { useAccountPreference, useAnonymously } = options || {};
  const { id } = useParams<{ id: any }>();
  const { inviteToken } = useInviteToken();

  const { data: account } = useGetAccountWithTokenQuery(
    {
      id,
      inviteToken,
    },
    { skip: useAnonymously || !id },
  );

  const currentLocale: Locale = useMemo(() => {
    // Spanish and English only translations available to-date.
    const locale = navigator.language.startsWith('es') ? Locale.es : Locale.en;
    if (useAnonymously) {
      return locale;
    }
    return useAccountPreference ? account?.language || locale : locale;
  }, [account, useAnonymously, useAccountPreference]);

  const translate = useMemo(() => {
    return (key: string, translations: Record<Locale, Record<string, string>>, preferredLocale?: Locale) => {
      if (preferredLocale) {
        return translations[preferredLocale]?.[key];
      }
      return translations[currentLocale]?.[key] || translations[Locale.en]?.[key] || key;
    };
  }, [currentLocale]);

  return {
    translate,
    locale: currentLocale,
  };
}
